import { ArrayPath, Control, FieldArray, FieldValues, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, IconButton, Typography } from '@mui/material'

import { CURRENCY } from '../../../constants/currency'
import { EMPTY_SKIER } from '../../../service/types'
import { TextFieldController } from '../fields/TextFieldController'

type Props<T extends FieldValues> = {
  control: Control<T>
  name: ArrayPath<T>
  prices?: number[]
  disabled?: boolean
}

export const SkierFieldArray = <T extends FieldValues>({ control, name, prices, disabled }: Props<T>) => {
  const { t } = useTranslation()

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  })

  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ mt: 0.5 }}>
      {fields.map((field, index) => (
        <Box key={field.id} display="flex" gap={1} alignItems="center">
          <Box flex={2}>
            <TextFieldController
              name={`${name}.${index}.name` as any}
              control={control}
              placeholder={t('requests.form.skierName')}
              disabled={disabled}
            />
          </Box>
          <Box flex={1}>
            <TextFieldController
              name={`${name}.${index}.age` as any}
              control={control}
              type="number"
              placeholder={t('requests.form.skierAge')}
              disabled={disabled}
            />
          </Box>
          {prices?.[index] !== undefined && (
            <Typography
              variant="body2"
              sx={{ minWidth: 70, textAlign: 'right', color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {prices[index]} {CURRENCY}
            </Typography>
          )}
          {fields.length > 1 && !disabled && (
            <IconButton size="small" onClick={() => remove(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}
      {!disabled && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append(EMPTY_SKIER as FieldArray<T, ArrayPath<T>>)}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('requests.form.addSkier')}
        </Button>
      )}
    </Box>
  )
}
